// Timeline sans l'effet scroll horizontal 
export default class TimelineModule {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('.timeline-track');
    this.slides = [...wrapper.querySelectorAll('.timeline-slide')];
    this.dates = [...wrapper.querySelectorAll('.date-item')];
    this.leftArrow = wrapper.querySelector('.arrow.left');
    this.rightArrow = wrapper.querySelector('.arrow.right');
    this.panel = document.querySelector('.panel-overlay');
    this.panelContent = this.panel?.querySelector('.panel-content__elements');

    this.index = 0;
    this.isPanelOpen = false;
    this.slideObserver = null;

    this.initMobileFixedNav();

    if (!this.slides.length) return;

    this.bindEvents();
    this.bindPanelEvents();
    this.update();

    // Mobile only
    if (this.isMobile()) {
      this.initMobileScrollSpy();
      window.addEventListener(
        'scroll',
        this.throttle(() => this.onMobileScroll(), 50)
      );
    }
  }

  // -------------------- Helpers --------------------
  isMobile() {
    return window.matchMedia('(max-width: 1024px)').matches;
  }

  throttle(func, limit) {
    let lastFunc;
    let lastRan;
    return (...args) => {
      if (!lastRan) {
        func.apply(this, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  }

  // -------------------- Bind UI --------------------
  bindEvents() {
    // Dates click
    this.dates.forEach(date => {
      date.addEventListener('click', () => {
        if (this.isPanelOpen) return;

        const i = +date.dataset.index;

        if (this.isMobile()) {
          const nav = this.wrapper.querySelector('.timeline-nav');
          const navHeight = nav ? nav.offsetHeight : 0;

          const top =
            this.slides[i].getBoundingClientRect().top +
            window.scrollY -
            navHeight;

          window.scrollTo({ top, behavior: 'smooth' });
        } else {
          this.goTo(i);
        }
      });
    });

    // Arrows desktop only
    if (!this.isMobile()) {
      this.leftArrow?.addEventListener('click', () =>
        this.goTo(this.index - 1)
      );
      this.rightArrow?.addEventListener('click', () =>
        this.goTo(this.index + 1)
      );
    }
  }

  // -------------------- Panel --------------------
  bindPanelEvents() {
    if (!this.panel) return;

    document.querySelectorAll('.js-open-panel').forEach(btn => {
      btn.addEventListener('click', e => {
        this.openPanel(btn);
        e.stopPropagation();
      });
    });

    this.panel.querySelectorAll('.panel-close').forEach(btn => {
      btn.addEventListener('click', () => this.closePanel());
    });

    document.body.addEventListener('click', e => {
      if (
        this.isPanelOpen &&
        document.body.classList.contains('body-dimmed') &&
        !e.target.closest('.panel-content')
      ) {
        this.closePanel();
      }
    });
  }

  openPanel(btn) {
    const parent = btn.closest('.timeline-slide');
    if (!parent) return;

    this.isPanelOpen = true;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('body-dimmed');

    const image = btn.dataset.image || '';
    const date_start = parent.querySelector('.date-start')?.innerHTML || '';
    const date_end = parent.querySelector('.date-end')?.innerHTML || '';

    const image_panel = btn.dataset.panelImage || '';
    const content_panel = btn.dataset.panelContent || '';
    const title_panel = btn.dataset.panelTitle || '';
    const chiffre_panel = btn.dataset.panelChiffre || '';
    const title_legende_chiffre = btn.dataset.panelChiffreLegende || '';
    const cta_url = btn.dataset.panelCtaUrl || '';
    const cta_title = btn.dataset.panelCtaTitle || '';

    const pdfUrl = btn.dataset.panelPdfUrl || '';
    const pdfTitle = btn.dataset.panelPdfTitle || '';
    const pdfExt = btn.dataset.panelPdfExt || '';
    const pdfSize = btn.dataset.panelPdfSize || '';
    const pdfDate = btn.dataset.panelPdfDate || '';
    const pdfImage = btn.dataset.panelPdfImage || '';

    const cardTitle = btn.dataset.cardTitle || '';
    const cardText = btn.dataset.cardText || '';
    const cardImage = btn.dataset.cardImage || '';
    const cardLink = btn.dataset.cardLink || '';

    let panelHtml = `
      <div class="panel-image" style="background-image:url('${image}')">
        <div class="left-date">
          <span class="date-start">${date_start}</span>
          <span class="date-separator">-</span>
          <span class="date-end">${date_end}</span>
        </div>
      </div>

      <div class="panel-content__infos">
        <div class="panel-content__image-text">
          <div class="panel-content_image">
            <img src="${image_panel}" loading="lazy" alt="">
          </div>
          <div>
            <h2>${title_panel}</h2>
            <div>${content_panel}</div>
          </div>
        </div>

        <div class="pilier__key">
          <strong><p class="titre-chiffre">${chiffre_panel}</p></strong>
          <span>${title_legende_chiffre}</span>
        </div>
    `;

    if (cta_url && cta_title) {
      panelHtml += `<a href="${cta_url}" class="link link--primary">${cta_title}</a>`;
    }

    if (pdfUrl) {
      panelHtml += `
        <div class="card__pdf u-background-green">
          <a class="card__body" href="${pdfUrl}" target="_blank">
            ${pdfImage ? `<img class="card__visuel" src="${pdfImage}">` : ''}
            <div class="card">
              <div class="card__content">
                <h3 class="u-c-white heading-pdf">${pdfTitle}</h3>
                <p class="u-c-white teasing-pdf">${pdfDate} · ${pdfExt} · ${pdfSize}</p>
              </div>
              <img class="card__icon" src="/wp-content/themes/albioma-com/static/assets/favicons/download.png">
            </div>
          </a>
        </div>`;
    }

    if (cardLink) {
      panelHtml += `
        <div class="card__pdf u-background-grey-light">
          <a class="card__body" href="${cardLink}" target="_blank">
            ${cardImage ? `<img class="card__visuel" src="${cardImage}">` : ''}
            <div class="card">
              <div class="card__content">
                <h3 class="u-c-black heading-pdf">${cardTitle}</h3>
                <p class="u-c-black teasing-pdf">${cardText}</p>
              </div>
              <img class="card__icon" src="/wp-content/themes/albioma-com/static/assets/favicons/arrow-right.png">
            </div>
          </a>
        </div>`;
    }

    panelHtml += '</div>';

    this.panelContent.innerHTML = panelHtml;
    this.panel.classList.add('active');
  }

  closePanel() {
    this.panel.classList.remove('active');
    this.isPanelOpen = false;
    document.body.style.overflow = '';
    document.body.classList.remove('body-dimmed');
  }

  // -------------------- Desktop Navigation --------------------
  goTo(i) {
    if (i < 0 || i >= this.slides.length || i === this.index) return;
    this.index = i;
    this.update();
  }

  update() {
    if (this.isMobile()) {
      this.track.style.transform = 'none';
    } else {
      // const offset = this.index * this.wrapper.offsetWidth;
      // this.track.style.transform = `translateX(-${offset}px)`;
      const slideWidth = this.slides[0].offsetWidth;
      const offset = this.index * slideWidth;
      this.track.style.transform = `translateX(-${offset}px)`;

    }

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === this.index);
      slide.classList.toggle('prev', i < this.index);
      slide.classList.toggle('next', i > this.index);
    });

    this.dates.forEach((date, i) =>
      date.classList.toggle('active', i === this.index)
    );

    if (this.leftArrow) this.leftArrow.disabled = this.index === 0;
    if (this.rightArrow)
      this.rightArrow.disabled = this.index === this.slides.length - 1;
  }

  // -------------------- Mobile Scroll Sync --------------------
  initMobileScrollSpy() {
    if (!this.isMobile()) return;

    this.slideObserver = new IntersectionObserver(
      entries => {
        if (!this.isMobile() || this.isPanelOpen) return;

        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = this.slides.indexOf(entry.target);
            if (index !== -1 && index !== this.index) {
              this.index = index;
              this.dates.forEach((date, i) =>
                date.classList.toggle('active', i === index)
              );
            }
          }
        });
      },
      {
        root: null,
        threshold: 0.6
      }
    );

    this.slides.forEach(slide => this.slideObserver.observe(slide));
  }

  // -------------------- Mobile Fixed Nav --------------------
  initMobileFixedNav() {
    if (!this.isMobile()) return;

    this.nav = this.wrapper.querySelector('.timeline-nav');
    this.placeholder = this.wrapper.querySelector('.timeline-nav-placeholder');
    this.header = document.querySelector('header, .site-header');

    if (!this.nav || !this.placeholder) return;

    this.updateMobileNavValues();

    window.addEventListener('resize', () => {
      if (!this.isMobile()) return;
      this.updateMobileNavValues();
      this.onMobileScroll();
    });
  }

  updateMobileNavValues() {
    this.headerHeight = this.header ? this.header.offsetHeight : 0;
    this.navHeight = this.nav.offsetHeight;

    const rect = this.nav.getBoundingClientRect();
    this.navTop = rect.top + window.scrollY;

    document.documentElement.style.setProperty(
      '--header-height',
      `${this.headerHeight}px`
    );
  }

  onMobileScroll() {
    if (!this.isMobile()) return;

    const shouldFix = window.scrollY >= this.navTop - this.headerHeight;

    if (shouldFix) {
      this.nav.classList.add('is-fixed');
      this.placeholder.style.height = `${this.navHeight}px`;
    } else {
      this.nav.classList.remove('is-fixed');
      this.placeholder.style.height = '0';
    }
  }
}




// Timeline avec effet scroll horizontal 

// export default class TimelineModule {
//   constructor(wrapper) {
//     this.wrapper = wrapper;
//     this.track = wrapper.querySelector('.timeline-track');
//     this.slides = [...wrapper.querySelectorAll('.timeline-slide')];
//     this.dates = [...wrapper.querySelectorAll('.date-item')];
//     this.leftArrow = wrapper.querySelector('.arrow.left');
//     this.rightArrow = wrapper.querySelector('.arrow.right');
//     this.panel = document.querySelector('.panel-overlay');
//     this.panelContent = this.panel?.querySelector('.panel-content__elements');

//     this.index = 0;
//     this.isLocked = false;
//     this.isPaused = false;
//     this.isPanelOpen = false;
//     this.lastScrollY = window.scrollY;
//     this.buffer = 10;

//     this.initMobileFixedNav();

//     if (!this.slides.length) return;

//     this.bindEvents();
//     this.bindPanelEvents();
//     this.update();

//     // Scroll listener pour verrou horizontal
//     window.addEventListener('scroll', this.throttle(() => this.checkLockScroll(), 50));
//     this.wrapper.addEventListener('wheel', e => this.onWheel(e), { passive: false });
//   }

//   // -------------------- Helpers --------------------
//   isMobile() {
//     return window.matchMedia("(max-width: 1024px)").matches;
//   }

//   throttle(func, limit) {
//     let lastFunc;
//     let lastRan;
//     return (...args) => {
//       if (!lastRan) {
//         func.apply(this, args);
//         lastRan = Date.now();
//       } else {
//         clearTimeout(lastFunc);
//         lastFunc = setTimeout(() => {
//           if ((Date.now() - lastRan) >= limit) {
//             func.apply(this, args);
//             lastRan = Date.now();
//           }
//         }, limit - (Date.now() - lastRan));
//       }
//     };
//   }

//   // -------------------- Bind UI --------------------
//   bindEvents() {
//     // Clic sur les dates
//     this.dates.forEach(date => {
//       date.addEventListener('click', () => {
//         if (this.isPanelOpen) return;
//         const i = +date.dataset.index;
        
//         if (this.isMobile()) {
//         // ✅ Scroll avec offset pour la nav sticky
//         const navHeight = this.wrapper.querySelector('.timeline-nav').offsetHeight;
//         const top = this.slides[i].getBoundingClientRect().top + window.scrollY - navHeight;
//         window.scrollTo({ top, behavior: 'smooth' });

//         this.dates.forEach(d => d.classList.remove('active'));
//         date.classList.add('active');
//         } else {
//         this.goTo(i);
//         }

//       });
//     });

//     // Flèches
//     if (!this.isMobile()) {
//       this.leftArrow?.addEventListener('click', () => this.goTo(this.index - 1));
//       this.rightArrow?.addEventListener('click', () => this.goTo(this.index + 1));
//     }
//   }

//   bindPanelEvents() {
//     if (!this.panel) return;

//     // Ouverture du panel
//     document.querySelectorAll('.js-open-panel').forEach(btn => {
//       btn.addEventListener('click', e => {
//         this.openPanel(btn);
//         e.stopPropagation();
//       });
//     });

//     // Fermeture du panel
//     this.panel.querySelectorAll('.panel-close').forEach(btn => {
//       btn.addEventListener('click', () => this.closePanel());
//     });

//     // Fermeture si clic hors panel
//     document.body.addEventListener('click', e => {
//       if (this.isPanelOpen && document.body.classList.contains('body-dimmed')) {
//         if (!e.target.closest('.panel-content')) this.closePanel();
//       }
//     });
//   }

//   openPanel(btn) {
//     const parent = btn.closest('.timeline-slide');
//     if (!parent) return;
//     this.isPanelOpen = true;
//     document.body.style.overflow = 'hidden';
//     document.body.classList.add('body-dimmed');

//     // Récupération des données
//     const image = btn.dataset.image || '';
//     const date_start = parent.querySelector('.date-start')?.innerHTML || '';
//     const date_end = parent.querySelector('.date-end')?.innerHTML || '';
//     const image_panel = btn.dataset.panelImage || '';
//     const content_panel = btn.dataset.panelContent || '';
//     const title_panel = btn.dataset.panelTitle || '';
//     const chiffre_panel = btn.dataset.panelChiffre || '';
//     const title_legende_chiffre = btn.dataset.panelChiffreLegende || '';
//     const cta_url = btn.dataset.panelCtaUrl || '';
//     const cta_title = btn.dataset.panelCtaTitle || '';
//     const pdfUrl = btn.dataset.panelPdfUrl || '';
//     const pdfTitle = btn.dataset.panelPdfTitle || '';
//     const pdfExt = btn.dataset.panelPdfExt || '';
//     const pdfSize = btn.dataset.panelPdfSize || '';
//     const pdfDate = btn.dataset.panelPdfDate || '';
//     const pdfImage = btn.dataset.panelPdfImage || '';
//     const cardTitle = btn.dataset.cardTitle || '';
//     const cardText = btn.dataset.cardText || '';
//     const cardImage = btn.dataset.cardImage || '';
//     const cardLink = btn.dataset.cardLink || '';

//     let panelHtml = `
//             <div class="panel-image" style="background-image: url('${image}');">
//                 <div class="left-date">
//                     <span class="date-start">${date_start}</span> 
//                     <span class="date-separator">-</span>
//                     <span class="date-end">${date_end}</span>
//                 </div>
//             </div>
//             <div class="panel-content__infos">
//                 <div class="panel-content__image-text">
//                     <div class="panel-content_image">
//                         <img src="${image_panel}" alt="" loading="lazy">
//                     </div>
//                     <div>
//                         <h2>${title_panel}</h2>
//                         <div>${content_panel}</div>
//                     </div>
//                 </div>
//                 <div class="pilier__key">
//                     <strong><p class="titre-chiffre">${chiffre_panel}</p></strong>
//                     <span>${title_legende_chiffre}</span>
//                 </div>
//         `;

//         if (cta_url && cta_title) {
//             panelHtml += `<a href="${cta_url}" class="link link--primary">${cta_title}</a>`;
//         }

//         if (pdfUrl) {
//             panelHtml += `
//             <div class="card__pdf u-background-green">
//                 <a class="card__body" href="${pdfUrl}" target="_blank">
//                     ${pdfImage ? `<img class="card__visuel" src="${pdfImage}" alt="">` : ''}
//                     <div class="card">
//                         <div class="card__content">
//                             <h3 class="u-c-white heading-pdf">${pdfTitle}</h3>
//                             <p class="card__meta teasing-pdf u-c-white">
//                                 ${pdfDate} · ${pdfExt} · ${pdfSize}
//                             </p>
//                         </div>
//                         <img class="card__icon" src="/wp-content/themes/albioma-com/static/assets/favicons/download.png" alt="Download PDF">
//                     </div>
//                 </a>
//             </div>
//             `;
//         }

//         if (cardLink) {
//             panelHtml += `
//             <div class="card__pdf u-background-grey-light">
//                 <a class="card__body" href="${cardLink}" target="_blank">
//                     ${cardImage ? `<img class="card__visuel" src="${cardImage}" alt="">` : ''}
//                     <div class="card">
//                         <div class="card__content">
//                             <h3 class="c-black heading-pdf">${cardTitle}</h3>
//                             <p class="c-black">${cardText}</p>
//                         </div>
//                         <img class="card__icon" src="/wp-content/themes/albioma-com/static/assets/favicons/arrow-right.png" alt="Link">
//                     </div>
//                 </a>
//             </div>
//             `;
//         }

//         panelHtml += `</div>`;
//         this.panelContent.innerHTML = panelHtml;
//         this.panel.classList.add('active');
//   }

//   closePanel() {
//     this.panel.classList.remove('active');
//     this.isPanelOpen = false;
//     document.body.style.overflow = '';
//     document.body.classList.remove('body-dimmed');
//   }

//   // -------------------- Navigation --------------------
//   goTo(i) {
//     if (i < 0 || i >= this.slides.length) return;
//     if (i === this.index) return;

//     this.index = i;
//     this.update();
//   }

//   update() {
//     // Décalage horizontal
//     if (this.isMobile()) {
//         this.track.style.transform = 'none';
//         return;
//     }
//     this.track.style.transform = `translateX(-${this.index * this.wrapper.offsetWidth}px)`;
//     // this.track.style.transform = `translateX(-${this.index * 100}vw)`;

//     // Gestion classes
//     this.slides.forEach((slide, i) => {
//       slide.classList.toggle('active', i === this.index);
//       slide.classList.toggle('prev', i < this.index);
//       slide.classList.toggle('next', i > this.index);
//     });
//     this.dates.forEach((date, i) => date.classList.toggle('active', i === this.index));

//     // Gestion flèches
//     if (this.leftArrow) this.leftArrow.disabled = this.index === 0;
//     if (this.rightArrow) this.rightArrow.disabled = this.index === this.slides.length - 1;
//   }

//   // -------------------- Scroll lock --------------------
//   checkLockScroll() {
//     if (this.isMobile() || this.isPanelOpen) return;

//     const rect = this.wrapper.getBoundingClientRect();
//     const scrollDir = window.scrollY - this.lastScrollY > 0 ? 'down' : 'up';
//     this.lastScrollY = window.scrollY;

//     const first = this.index === 0;
//     const last = this.index === this.slides.length - 1;

//     // Lock entrée haut/bas
//     if (!this.isLocked && first && rect.top <= this.buffer && scrollDir === 'down') this.lock();
//     if (!this.isLocked && last && rect.bottom >= window.innerHeight - this.buffer && scrollDir === 'up') this.lock();

//     // Unlock sortie haut/bas
//     if (this.isLocked && first && scrollDir === 'up' && rect.top > this.buffer) this.unlock();
//     if (this.isLocked && last && scrollDir === 'down' && rect.bottom < window.innerHeight - this.buffer) this.unlock();
//   }

//   lock() {
//     this.isLocked = true;
//     document.body.style.overflow = 'hidden';
//     window.scrollTo({ top: this.wrapper.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
//   }

//   unlock() {
//     this.isLocked = false;
//     document.body.style.overflow = '';
//   }

//   onWheel(e) {
//     if (!this.isLocked || this.isPanelOpen) return;
//     if (this.isMobile() || !this.isLocked || this.isPanelOpen) return; // ne rien faire sur mobile

//     e.preventDefault();

//     if (this.isPaused) return; // pause avant de calculer next

//     // Normalisation du delta
//     const direction = e.deltaY > 0 ? 1 : -1;
//     const nextIndex = this.index + direction;
//     const lastIndex = this.slides.length - 1;

//     // Déblocage vertical aux extrémités
//     if ((nextIndex < 0 && direction < 0) || (nextIndex > lastIndex && direction > 0)) {
//         this.unlock();
//         window.scrollBy(0, direction); // petit push pour relancer scroll navigateur
//         return;
//     }

//     if (nextIndex < 0 || nextIndex > lastIndex) return;

//     // Appliquer la pause **avant** l'update
//     this.isPaused = true;
//     setTimeout(() => (this.isPaused = false), 800);

//     this.index = nextIndex;
//     this.update();
//     }

//     initMobileFixedNav() {
//     if (!this.isMobile()) return;

//     this.nav = this.wrapper.querySelector('.timeline-nav');
//     this.placeholder = this.wrapper.querySelector('.timeline-nav-placeholder');
//     this.header = document.querySelector('header, .site-header');

//     if (!this.nav || !this.placeholder) return;

//     this.updateMobileNavValues();

//     window.addEventListener('scroll', () => this.onMobileScroll());
//     window.addEventListener('resize', () => {
//         if (!this.isMobile()) return;
//         this.updateMobileNavValues();
//         this.onMobileScroll();
//     });
//     }
//     updateMobileNavValues() {
//         this.headerHeight = this.header ? this.header.offsetHeight : 0;
//         this.navHeight = this.nav.offsetHeight;

//         const rect = this.nav.getBoundingClientRect();
//         this.navTop = rect.top + window.scrollY;

//         document.documentElement.style.setProperty(
//             '--header-height',
//             `${this.headerHeight}px`
//         );
//     }
//     onMobileScroll() {
//         if (!this.isMobile()) return;

//         const shouldFix =
//             window.scrollY >= this.navTop - this.headerHeight;

//         if (shouldFix) {
//             this.nav.classList.add('is-fixed');
//             this.placeholder.style.height = `${this.navHeight}px`;
//         } else {
//             this.nav.classList.remove('is-fixed');
//             this.placeholder.style.height = '0';
//         }
//     }

// }
